import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TenantContextService } from '../tenant/tenant-context.service';
import { SKIP_ACT_AS } from '../http/act-as.context';

/**
 * Rutas que NO pertenecen a ningún tenant y por tanto no admiten act-as.
 *
 * Los documentos legales (M20 / ADR-0008) son un catálogo global o por país y la
 * gestión de super admins administra usuarios globales (`tenant_id NULL`): en
 * ninguno de los dos casos hay un tenant sobre el que "actuar como", y el backend
 * rechaza —o ignora en silencio— las cabeceras cross-tenant en estas rutas. Son
 * los módulos del portal donde SUPER_ADMIN opera sin act-as.
 *
 * En super-admins la justificación del reset viaja en el BODY, precisamente
 * porque no hay act-as al que colgar `X-Cross-Tenant-Justification`.
 */
export const TENANT_AGNOSTIC_PATHS: readonly string[] = ['/v1/legal-documents', '/v1/super-admins'];

/**
 * `url.includes(p)` casaría por subcadena arbitraria: una futura ruta como
 * `/v1/legal-documents-templates` activaría la excepción sin querer y
 * perdería las cabeceras de act-as en silencio. Se ancla el matching al
 * pathname (sin origen ni query string) y se exige que, tras el prefijo,
 * venga un `/` (sub-ruta legítima, p.ej. `/v1/legal-documents/:id/acceptances`)
 * o el final de la cadena — nunca otro carácter de un segmento más largo.
 */
function isTenantAgnostic(url: string): boolean {
  const pathname = new URL(url, 'http://localhost').pathname;
  return TENANT_AGNOSTIC_PATHS.some((prefix) => {
    const idx = pathname.indexOf(prefix);
    if (idx === -1) return false;
    const nextChar = pathname[idx + prefix.length];
    return nextChar === undefined || nextChar === '/';
  });
}

/**
 * TenantInterceptor — agrega los headers de tenant a cada request.
 *
 *   X-Tenant-Slug: <slug>
 *     El backend lo usa SOLO en rutas públicas de auth (login) para resolver a
 *     qué tenant pertenece el usuario. En rutas protegidas el tenant viene del
 *     claim `tenant_id` del JWT, no de este header.
 *
 *   X-Act-As-Tenant-Id: <uuid>  +  X-Cross-Tenant-Justification: <texto ≥5 chars>
 *     Cuando un SUPER_ADMIN opera la flota de un tenant (actingTenant seteado).
 *     El backend valida el tenant, scopea RLS a él y audita el acceso. Sin estos
 *     headers, el super_admin recibe ACT_AS_TENANT_REQUIRED en recursos
 *     tenant-scoped (vehicles/drivers).
 *
 *   X-Cross-Tenant-Justification (one-shot)
 *     Para el módulo audit (flag crossTenant por query). Se limpia tras el request.
 *
 *   Excepción: las rutas de `TENANT_AGNOSTIC_PATHS` (documentos legales) no llevan
 *   ninguna cabecera cross-tenant y no consumen la justificación one-shot.
 *
 *   Excepción por petición: `SKIP_ACT_AS` (`core/http/act-as.context.ts`). Una
 *   ruta tenant-scoped puede pedir explícitamente que ESTA llamada en concreto
 *   no lleve act-as — mismo criterio que una ruta agnóstica: ni cabeceras ni
 *   consumo del one-shot. Para llamadas auxiliares de catálogo desde pantallas
 *   que no operan tenant-scoped (ver el comentario del token).
 *
 *   Precedencia: una cabecera que la petición ya trae expresa una intención más
 *   específica que el contexto ambiental y NUNCA se pisa (p.ej. CredentialsService
 *   fija act-as del tenant que se está viendo en Back Office, que puede no ser el
 *   del act-as global). Las dos cabeceras act-as viajan en pareja: si la petición
 *   trae su propio `X-Act-As-Tenant-Id`, tampoco se completa la justificación
 *   desde el contexto — mezclar un id explícito con la justificación de otra
 *   sesión act-as dejaría un registro de auditoría engañoso.
 */
@Injectable({ providedIn: 'root' })
export class TenantInterceptor implements HttpInterceptor {
  private readonly tenant = inject(TenantContextService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const slug = this.tenant.tenantSlug();
    const acting = this.tenant.actingTenant();
    const oneShotJustification = this.tenant.crossTenantJustification();
    const agnostic = isTenantAgnostic(req.url) || req.context.get(SKIP_ACT_AS);

    const headers: Record<string, string> = {};
    if (slug && !req.headers.has('X-Tenant-Slug')) headers['X-Tenant-Slug'] = slug;

    if (!agnostic && !req.headers.has('X-Act-As-Tenant-Id')) {
      if (acting && !req.headers.has('X-Cross-Tenant-Justification')) {
        // Act-as: el super_admin opera la flota del tenant elegido (auditado).
        headers['X-Act-As-Tenant-Id'] = acting.id;
        headers['X-Cross-Tenant-Justification'] = acting.justification;
      } else if (
        !acting &&
        oneShotJustification &&
        oneShotJustification.length >= 5 &&
        !req.headers.has('X-Cross-Tenant-Justification')
      ) {
        // Audit cross-tenant (listar todos los tenants).
        headers['X-Cross-Tenant-Justification'] = oneShotJustification;
      }
    }

    const outReq = Object.keys(headers).length > 0 ? req.clone({ setHeaders: headers }) : req;

    // En rutas tenant-agnósticas no se consume el one-shot: si lo limpiáramos,
    // una llamada a documentos legales dejaría sin justificación a la petición
    // que el módulo audit acaba de preparar.
    if (agnostic) return next.handle(outReq);

    return next
      .handle(outReq)
      .pipe(tap({ complete: () => this.tenant.crossTenantJustification.set(null) }));
  }
}
